import { useSelector } from 'react-redux';
import { detectMarket, formatMarketCurrency, getCurrencyForMarket, MARKET_NORWAY, MARKET_SWEDEN } from '../utils/market';

/**
 * Custom hook to access pricing configuration from Redux
 * Pricing is fetched from backend at app startup and stored in Redux state
 * 
 * Usage:
 * const { bookCreationWithCopyPrice, additionalCopyPrice, getPrintPrice } = usePricing();
 * const total = getPrintPrice(3); // Total for 3 copies
 */
export const usePricing = () => {
  const pricingState = useSelector((state) => state.pricing);
  const market = pricingState?.pricing?.market || detectMarket();
  const fallbackRates = {
    de: 0.134,
    [MARKET_SWEDEN]: 1.495,
    [MARKET_NORWAY]: 1.47,
  };
  const conversionRate = fallbackRates[market] || 1;
  const fallbackPricing = {
    bookCreationWithCopyPrice: Number((160 * conversionRate).toFixed(2)),
    additionalCopyPrice: Number((80 * conversionRate).toFixed(2)),
    languageAddonPrice: 0,
    shippingPhysicalPrice: Number((60 * conversionRate).toFixed(2)),
    market,
    currency: getCurrencyForMarket(market),
  };
  const pricing = pricingState?.pricing?.market
    ? pricingState.pricing
    : fallbackPricing;

  const getPrintPrice = (copies) => {
    if (copies <= 0) return 0;
    if (copies === 1) return pricing.bookCreationWithCopyPrice;
    // 1 copy included in base, then additional copies at configured price
    return pricing.bookCreationWithCopyPrice + (copies - 1) * pricing.additionalCopyPrice;
  };

  const formatPrice = (amount) => formatMarketCurrency(amount, pricing.market || market);

  return {
    ...pricing,
    getPrintPrice,
    formatPrice,
    loading: pricingState?.loading || false,
    error: pricingState?.error || null,
  };
};
